import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { FaCircleInfo } from "react-icons/fa6";
import { FaHome } from "react-icons/fa";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { IoClose } from "react-icons/io5";

/*
 * Shared map controls styled to match the Ocean State page:
 *  - MapInfoControl : glass "i" button (bottom-left) with an info panel
 *  - MapLegendControl : collapsible glass legend box (bottom-right)
 *  - MapHomeControl : home button below the zoom controls that resets
 *    the map to its initial view
 */

export const glassStyle = {
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(10px) brightness(1.1)",
  WebkitBackdropFilter: "blur(10px) brightness(1.1)",
  border: "1px solid rgba(0, 0, 0, 0.08)",
  boxShadow:
    "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
  color: "#333333",
};

/* ------------------------------------------------------------------ */
/* Info button + panel — bottom-left                                   */
/* ------------------------------------------------------------------ */
const useNoMapClick = () =>
  useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);

export const MapInfoControl = ({ text, title, width = "320px" }) => {
  const [open, setOpen] = useState(false);
  const noClickRef = useNoMapClick();
  const containerRef = useRef(null);

  const setRefs = useCallback(
    (node) => {
      containerRef.current = node;
      noClickRef(node);
    },
    [noClickRef]
  );

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={setRefs} style={{ position: "absolute", bottom: 0, left: 0 }}>
      {open && (
        <div
          style={{
            position: "absolute",
            bottom: "70px",
            left: "12px",
            fontSize: "12px",
            zIndex: 1001,
            padding: "10px 14px",
            borderRadius: "10px",
            ...glassStyle,
            fontWeight: "500",
            width,
            maxWidth: "min(90vw, 480px)",
            lineHeight: "1.4",
          }}
        >
          {title && (
            <div
              style={{
                fontWeight: "700",
                fontSize: "12px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              {title}
            </div>
          )}
          {text}
        </div>
      )}
      <div
        style={{
          position: "absolute",
          bottom: "20px",
          left: "12px",
          zIndex: 1000,
          padding: "8px",
          borderRadius: "8px",
          fontSize: "15px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
          ...glassStyle,
        }}
        onClick={() => setOpen((prev) => !prev)}
        title="Information"
      >
        <FaCircleInfo />
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Legend section — reusable list of colored swatches                  */
/* ------------------------------------------------------------------ */
export const MapLegendSection = ({ title, items, shape = "circle" }) => (
  <div style={{ marginBottom: "14px" }}>
    {title && (
      <div
        style={{
          fontWeight: "700",
          marginBottom: "8px",
          fontSize: "11px",
          color: "#333333",
          letterSpacing: "0.5px",
          textTransform: "uppercase",
        }}
      >
        {title}
      </div>
    )}
    {items.map((item) => (
      <div
        key={item.label}
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "5px",
          gap: "8px",
        }}
      >
        {item.icon ? (
          <span style={{ width: "16px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {item.icon}
          </span>
        ) : (
          <span
            style={{
              width: "14px",
              height: shape === "line" ? "4px" : "14px",
              backgroundColor: item.color,
              borderRadius: shape === "circle" ? "50%" : shape === "line" ? "2px" : "3px",
              border: "1.5px solid rgba(0, 0, 0, 0.15)",
              flexShrink: 0,
              boxShadow: `0 0 6px ${item.color}80`,
            }}
          />
        )}
        <span
          style={{
            fontSize: "11px",
            lineHeight: "1.3",
            color: "#333333",
            fontWeight: "500",
          }}
        >
          {item.label}
        </span>
      </div>
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/* Collapsible legend box — bottom-right                               */
/* ------------------------------------------------------------------ */
export const MapLegendControl = ({
  sections,
  children,
  defaultOpen = false,
  width = "260px",
  position = "bottomright",
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const noClickRef = useNoMapClick();
  const containerRef = useRef(null);

  const setRefs = useCallback(
    (node) => {
      containerRef.current = node;
      noClickRef(node);
    },
    [noClickRef]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const anchor =
    position === "bottomleft"
      ? { bottom: "20px", left: "12px" }
      : { bottom: "20px", right: "12px" };

  return (
    <div
      ref={setRefs}
      style={{
        position: "absolute",
        ...anchor,
        zIndex: 1000,
        borderRadius: "12px",
        width,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        overflow: "hidden",
        transition: "all 0.3s ease",
        ...glassStyle,
        backdropFilter: "blur(12px) brightness(1.1)",
        WebkitBackdropFilter: "blur(12px) brightness(1.1)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 14px",
          borderBottom: isOpen ? "1px solid rgba(0, 0, 0, 0.1)" : "none",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div
          style={{
            fontWeight: "700",
            fontSize: "12px",
            color: "#333333",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          Legend
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {isOpen ? (
            <BsChevronUp size={16} color="#333333" />
          ) : (
            <BsChevronDown size={16} color="#333333" />
          )}
          {isOpen && (
            <IoClose
              size={16}
              color="#333333"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              style={{ cursor: "pointer" }}
            />
          )}
        </div>
      </div>

      {/* Content */}
      {isOpen && (
        <div
          style={{
            padding: "12px 14px",
            paddingTop: "8px",
            maxHeight: "320px",
            overflowY: "auto",
          }}
        >
          {sections &&
            sections.map((section, idx) => (
              <MapLegendSection
                key={section.title || idx}
                title={section.title}
                items={section.items}
                shape={section.shape}
              />
            ))}
          {children}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Home button — resets the map to its initial view.                   */
/* Placed just below the default leaflet zoom controls (top-left).     */
/* Pass center/zoom to override; otherwise the view is captured once   */
/* it stops changing (handles async/multi-step fitBounds on load).     */
/* ------------------------------------------------------------------ */
export const MapHomeControl = ({ center, zoom, bounds }) => {
  const map = useMap();
  const initialViewRef = useRef(null);

  useEffect(() => {
    // Explicit center/zoom override — no need to observe the map at all.
    if (center && typeof zoom === "number") {
      initialViewRef.current = { center, zoom };
      return undefined;
    }

    // Many pages set their initial view asynchronously (e.g. fitBounds once
    // data loads), sometimes in more than one step — a wide boundary fit
    // followed by a tighter data-driven fit. getCenter()/getZoom() also
    // throw until the map has *any* view (Leaflet: "Set map center and zoom
    // first"). So rather than capturing on the first "load"/move, keep
    // re-capturing on every view change and only lock it in once the view
    // has been stable for a short settle window — that's the view the user
    // actually first sees, not an intermediate fit.
    const SETTLE_DELAY = 600;
    let settleTimeout;
    let finalized = false;

    const finalize = () => {
      if (finalized) return;
      finalized = true;
      initialViewRef.current = { center: map.getCenter(), zoom: map.getZoom() };
      map.off("moveend", scheduleFinalize);
      map.off("load", scheduleFinalize);
    };

    const scheduleFinalize = () => {
      if (finalized) return;
      clearTimeout(settleTimeout);
      settleTimeout = setTimeout(finalize, SETTLE_DELAY);
    };

    if (map._loaded) {
      scheduleFinalize();
    } else {
      map.once("load", scheduleFinalize);
    }
    map.on("moveend", scheduleFinalize);

    return () => {
      clearTimeout(settleTimeout);
      map.off("moveend", scheduleFinalize);
      map.off("load", scheduleFinalize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep clicks on the control from reaching the map (some maps place
  // markers on click)
  const containerRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);

  const resetView = (e) => {
    e.stopPropagation();
    if (bounds) {
      map.fitBounds(bounds);
      return;
    }
    const view = initialViewRef.current;
    if (view) {
      map.setView(view.center, view.zoom);
    }
  };

  return (
    <div className="leaflet-top leaflet-left" style={{ pointerEvents: "none" }}>
      <div
        ref={containerRef}
        className="leaflet-control leaflet-bar"
        style={{
          marginTop: "80px",
          pointerEvents: "auto",
          background: "#fff",
          cursor: "pointer",
        }}
        onClick={resetView}
        title="Reset map to initial view"
      >
        <a
          href="#reset"
          role="button"
          aria-label="Reset map view"
          onClick={(e) => e.preventDefault()}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "30px",
            height: "30px",
            color: "#333",
            fontSize: "15px",
          }}
        >
          <FaHome />
        </a>
      </div>
    </div>
  );
};
